/**
 * RFC 9457 "Problem Details for HTTP APIs" types and parsing.
 *
 * The backend returns application/problem+json for every error response:
 * a stable machine `code` plus optional per-field `errors` that map onto
 * form fields.
 */

export interface FieldError {
  /** Dotted/indexed path into the request payload, e.g. "lines[2].qty". */
  field: string;
  /** Stable machine code for this field problem. */
  code: string;
  /** Human-readable message (server locale; UI prefers mapping `code`). */
  message: string;
}

export interface Problem {
  /** URI reference identifying the problem type. */
  type?: string;
  title: string;
  status: number;
  detail?: string;
  /** Stable machine code, e.g. "auth.invalid_credentials". */
  code?: string;
  errors?: FieldError[];
}

function isFieldError(x: unknown): x is FieldError {
  if (typeof x !== "object" || x === null) return false;
  const e = x as Record<string, unknown>;
  return typeof e.field === "string" && typeof e.code === "string" && typeof e.message === "string";
}

/**
 * Structural check, independent of content type — usable on any parsed JSON
 * value (error bodies occasionally arrive as plain application/json).
 */
export function isProblem(x: unknown): x is Problem {
  if (typeof x !== "object" || x === null || Array.isArray(x)) return false;
  const p = x as Record<string, unknown>;
  if (typeof p.title !== "string" || typeof p.status !== "number") return false;
  if (p.type !== undefined && typeof p.type !== "string") return false;
  if (p.detail !== undefined && typeof p.detail !== "string") return false;
  if (p.code !== undefined && typeof p.code !== "string") return false;
  if (p.errors !== undefined) {
    if (!Array.isArray(p.errors) || !p.errors.every(isFieldError)) return false;
  }
  return true;
}

/**
 * Read an application/problem+json (or structurally conforming
 * application/json) body from a response. Returns null when the response has
 * no parsable problem body. Reads from a clone, so the caller's body stream
 * stays untouched.
 */
export async function parseProblemResponse(res: Response): Promise<Problem | null> {
  const contentType = res.headers.get("content-type") ?? "";
  if (
    !contentType.includes("application/problem+json") &&
    !contentType.includes("application/json")
  ) {
    return null;
  }
  try {
    const body: unknown = await res.clone().json();
    return isProblem(body) ? body : null;
  } catch {
    return null;
  }
}
